import * as XLSX from 'xlsx';
import { getDownloadColumnsMap } from '../../../../utils/buildDownloadEndpoint';

function cleanHeaderCellValue(value: string): string {
  const trimmed = String(value).trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function buildBackendToUiKeyMap(): Map<string, string> {
  const map = new Map<string, string>();
  getDownloadColumnsMap().forEach((backendKey, uiKey) => {
    if (!map.has(backendKey)) {
      map.set(backendKey, uiKey);
    }
  });
  return map;
}

function mapHeaderToUiKey(header: string, backendToUi: Map<string, string>): string {
  const clean = cleanHeaderCellValue(header);
  return backendToUi.get(clean) ?? clean;
}

function parseTsvText(raw: string, backendToUi: Map<string, string>): Record<string, string>[] {
  const lines = raw.split(/\r?\n/).filter(line => line.trim().length > 0);
  if (lines.length < 2) return [];

  const headers = lines[0].split('\t').map(h => mapHeaderToUiKey(h, backendToUi));

  return lines.slice(1).map(line => {
    const cells = line.split('\t');
    const row: Record<string, string> = {};
    headers.forEach((key, index) => {
      row[key] = cells[index] ?? '';
    });
    return row;
  });
}

function parseXlsxBuffer(buffer: ArrayBuffer, backendToUi: Map<string, string>): Record<string, string>[] {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return [];

  const worksheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1, defval: '' });
  if (!rows.length || !Array.isArray(rows[0])) return [];

  const headers = rows[0].map((header: unknown) =>
    mapHeaderToUiKey(String(header ?? ''), backendToUi),
  );

  return rows.slice(1).map((cells: unknown[]) => {
    const row: Record<string, string> = {};
    headers.forEach((key, index) => {
      row[key] = cells[index] != null ? String(cells[index]) : '';
    });
    return row;
  });
}

export async function parseDownloadBlob(
  blob: Blob,
  extension: 'tsv' | 'xlsx' = 'tsv',
): Promise<Record<string, string>[]> {
  const backendToUi = buildBackendToUiKeyMap();

  if (extension === 'tsv') {
    return parseTsvText(await blob.text(), backendToUi);
  }

  return parseXlsxBuffer(await blob.arrayBuffer(), backendToUi);
}
